// src/hooks/useCanvasHistory.js
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { sanitizeHead, buildIframeDoc } from '../utils/htmlSanitizer.js';

export default function useCanvasHistory({
  chatId,
  canvasContent,
  setCanvasContent,
  lastHtml, setLastHtml,
  lastCss,  setLastCss,
  lastJs,   setLastJs,
  collabSessionActive,
  isController,
  sessionRoom,
  socket,
  ownerName,
}) {
  const navigate = useNavigate();

  const [history, setHistory] = useState([]);
  const [timestamps, setTimestamps] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [versionRows, setVersionRows] = useState([]);

  // ─── clear out everything when we switch chats ───
  useEffect(() => {
    setHistory([]);
    setTimestamps([]);
    setHistoryIndex(-1);
    setCanvasContent('');
    setLastHtml('');
    setLastCss('');
    setLastJs('');
  }, [chatId, setCanvasContent, setLastHtml, setLastCss, setLastJs]);

  // — hydrate from DB on load or when chatId changes
  useEffect(() => {
    if (!chatId || (collabSessionActive && !isController)) return;

    fetch(`/api/chats/${chatId}/versions`)
      .then(r => r.json())
      .then(rows => {
        if (!rows.length) return;
        setVersionRows(rows);
        const docs = rows.map(v =>
          buildIframeDoc({
            cleanHead: sanitizeHead(''),
            cssCode:   v.css,
            bodyInner: v.html,
            jsCode:    v.js,
          })
        );
        const times = rows.map(v =>
          new Date(v.created_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})
        );
        setHistory(docs);
        setTimestamps(times);
        setHistoryIndex(docs.length - 1);
        setCanvasContent(docs[docs.length - 1]);

        const last = rows[rows.length - 1];
        setLastHtml(last.html);
        setLastCss(last.css);
        setLastJs(last.js);
      })
      .catch(console.error);
  }, [chatId, collabSessionActive, isController]);

  // — listen for remote version changes
  useEffect(() => {
    function onVersionChange({ room, versionIndex, clientId }) {
      if (room !== sessionRoom) return;
      if (clientId === socket.id) return;
      if (isController) return;

      fetch(`/api/chats/${room}/versions`)
        .then(r => r.json())
        .then(rows => {
          const docs = rows.map(v =>
            buildIframeDoc({
              cleanHead: sanitizeHead(''),
              cssCode:   v.css,
              bodyInner: v.html,
              jsCode:    v.js,
            })
          );
          setVersionRows(rows);
          const times = rows.map(v =>
            new Date(v.created_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})
          );
          setHistory(docs);
          setTimestamps(times);
          setHistoryIndex(versionIndex);
          setCanvasContent(docs[versionIndex]);
        })
        .catch(console.error);
    }

    socket.on('version_change', onVersionChange);
    return () => socket.off('version_change', onVersionChange);
  }, [socket, sessionRoom, isController, setCanvasContent]);

  // — helper to jump around
  const jumpToVersion = useCallback(i => {
    const doc = history[i];
    if (!doc) return;
    setHistoryIndex(i);
    setCanvasContent(doc);

    const h = (doc.match(/<head[^>]*>([\s\S]*?)<\/head>/i)||[])[1]||'';
    const b = (doc.match(/<body[^>]*>([\s\S]*?)<\/body>/i)||[])[1]||'';
    // setLastHtml(   b );
    // setLastCss(    '' );
    // setLastJs(     '' );

    // grab the exact HTML/CSS/JS that you saved in the DB:
    const version = versionRows[i] || {};
    setLastHtml(version.html || '');
    setLastCss (version.css  || '');
    setLastJs  (version.js   || '');

    // (you can parse out CSS/JS if you wrapped those in <style>/<script>)
  }, [history, versionRows, setCanvasContent, setLastHtml, setLastCss, setLastJs]);

  // — after sending a new AI response, call this to append a version
  const appendVersion = async ({
    htmlCode,
    cssCode,
    jsCode,
    userMessageId,
    aiMessageId,
    persist = true
  }) => {
    console.log('📌 appendVersion()', { htmlCode, cssCode, jsCode, userMessageId, aiMessageId })
    // build the iframe document
    const cleanDoc = buildIframeDoc({
      cleanHead: sanitizeHead( (htmlCode.match(/<head[^>]*>([\s\S]*?)<\/head>/i)||[])[1]||'' ),
      cssCode,
      bodyInner: (htmlCode.match(/<body[^>]*>([\s\S]*?)<\/body>/i)||[])[1]||htmlCode,
      jsCode,
    });
    const timeStr = new Date().toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'});

    const truncated   = history.slice(0, historyIndex + 1);
    const newHistory  = [...truncated, cleanDoc];
    const newIndex    = newHistory.length - 1;

    setHistory(newHistory);
    setTimestamps(ts => [...ts.slice(0, historyIndex + 1), timeStr]);
    setHistoryIndex(newIndex);
    setCanvasContent(cleanDoc);

    // 2) **also** update the code‐tab state
    setLastHtml(htmlCode  || '');
    setLastCss (cssCode   || '');
    setLastJs  (jsCode    || '');

    if (isController) {
      socket.emit('version_change', {
        room:         sessionRoom,
        versionIndex: newIndex,
        clientId:     socket.id,
      });
    }

    // persist to DB
    if (chatId && persist && (htmlCode || cssCode || jsCode)) {
      await fetch(`/api/chats/${chatId}/versions`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({ html:htmlCode, css:cssCode, js:jsCode, userMessageId, aiMessageId })
      });
    }

    setVersionRows(vs => [
      ...vs.slice(0, historyIndex+1),
      { html: htmlCode, css: cssCode, js: jsCode, created_at: new Date().toISOString() }
      ]);

    // update URL
    if (ownerName && chatId != null) {
      navigate(`/${ownerName}/${chatId}/${newIndex}`, { replace: true });
    }
  };

  return {
    history,
    timestamps,
    historyIndex,
    jumpToVersion,
    appendVersion,
  };
}
